import React from "react"
import utilStyles from "../../asset/styles/util/util.module.scss"
import Grid from "@mui/material/Grid"
import Table, { TableColumn } from "component/table/table"
import InputText from "component/inputText/inputText"
import styles from "../../asset/styles/packagesDetail/packagesDetail.module.scss"
import { PackageDetailModel } from "./packagesDetailTypes"

/** Row shape for package pricing table: one row with keys "1"|"2"|... (seating_type_id) and price values */
type PackagePricingRow = Record<string, number>

interface PackagePricingProps {
    data: PackageDetailModel
    onChange: (updates: Partial<PackageDetailModel>) => void
    error?: string
}

const PackagePricing: React.FC<PackagePricingProps> = ({
    data,
    onChange,
    error,
}) => {
    const prices = data.package_trip_price ?? []

    const updatePrice = (seating_type_id: number, value: number) => {
        const updated = prices.map((p) =>
            p.seating_type_id === seating_type_id ? { ...p, price: value } : p
        )
        onChange({ package_trip_price: updated })
    }

    const getPrice = (seating_type_id: number) => {
        const item = prices.find((p) => p.seating_type_id === seating_type_id)
        return item?.price ?? 0
    }

    const colWidth =
        prices.length > 0 ? `${100 / prices.length}%` : "16.66%"

    const pricingRow: PackagePricingRow = prices.reduce(
        (acc, p) => {
            acc[String(p.seating_type_id)] = getPrice(p.seating_type_id)
            return acc
        },
        {} as PackagePricingRow
    )

    const columns: TableColumn<PackagePricingRow>[] = prices.map((entry) => {
        const price = getPrice(entry.seating_type_id)
        const hasError =
            price == null ||
            !Number.isFinite(price) ||
            price < 0 ||
            price === 0
        return {
            key: String(entry.seating_type_id),
            label: (
                <>
                    {entry.seating_type_name}{" "}
                    <span style={{ color: "#dc3545" }}>*</span>
                </>
            ),
            align: "left" as const,
            width: colWidth,
            render: (value: number) => {
                const displayValue =
                    value == null || !Number.isFinite(value) || value === 0
                        ? ""
                        : String(value)
                return (
                    <InputText
                        label=""
                        value={displayValue}
                        onChange={(e) =>
                            updatePrice(
                                entry.seating_type_id,
                                Number(e.target.value) || 0
                            )
                        }
                        placeholder="Total price"
                        type="number"
                        error={error && hasError ? "Required" : undefined}
                    />
                )
            },
        }
    })

    return (
        <div className={utilStyles.pageContainerContent}>
            <div className={styles.sectionHeader}>
                <h2 className={styles.sectionTitle}>Package Pricing</h2>
            </div>
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 12 }}>
                    <Table<PackagePricingRow>
                        minWidth={100}
                        columns={columns}
                        data={[pricingRow]}
                        keyExtractor={() => "package-pricing"}
                        className={styles.seatingTable}
                    />
                </Grid>
            </Grid>
        </div>
    )
}

export default PackagePricing
